import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { useAtom } from "jotai";
import { labelsAtom } from "../../-Atoms";
import { listSupplyFoldersWithSmartDepth, countFolderContents, getDropboxLink } from "../../DropboxFunctions";

const checkClientSelectsExists = async (jobID) => {
  try {
    const clientSelectsRef = doc(db, "ClientSelects", jobID);
    const clientSelectsDoc = await getDoc(clientSelectsRef);
    return clientSelectsDoc.exists();
  } catch (error) {
    console.error("Error checking ClientSelects document:", error);
    return false;
  }
};




const SUBFOLDER_COLLAPSE_THRESHOLD = 4;
const DEFAULT_COLLAPSIBLE_PARENT_NAME = "All Folders";
const DEFAULT_COLLAPSIBLE_PARENT_PATH = "/supply";

const DEFAULT_VISIBLE_FOLDERS = [
    "/storyboards",
    "/storyboard selects",
    "/quickproofs",
    "/content",
    "/early supply",
];

// Default folders that are visible before "Supplied" label is added
const DEFAULT_SUPPLIED_FOLDERS = [
  "/storyboards",
  "/storyboard selects",
  "/quickproofs",
  "/content",
  "/early supply",
];

const formatFolderDisplayName = (name) => {
  if (!name) {
    return DEFAULT_COLLAPSIBLE_PARENT_NAME;
  }
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const getParentPathFromRelativePath = (relativePath) => {
  if (!relativePath) {
    return null;
  }

  const segments = relativePath.split("/").filter(Boolean);
  if (segments.length <= 1) {
    return null;
  }

  segments.pop();
  return `/${segments.join("/")}`;
};

const getCollapsibleParentMetadata = (subfolders) => {
  if (!subfolders || subfolders.length === 0) {
    return null;
  }

  const parentPaths = subfolders
    .map((folder) => getParentPathFromRelativePath(folder.path))
    .filter(Boolean);

  if (parentPaths.length === 0) {
    return null;
  }

  const firstParentPath = parentPaths[0];
  const sharesSameParent = parentPaths.every(
    (path) => path === firstParentPath
  );

  if (!sharesSameParent) {
    return null;
  }

  const explicitParentName = subfolders.find(
    (folder) => folder.parentFolder
  )?.parentFolder;

  const derivedParentName = firstParentPath
    .split("/")
    .filter(Boolean)
    .pop();

  const displayName = explicitParentName || derivedParentName;

  if (!displayName) {
    return null;
  }

  return {
    name: formatFolderDisplayName(displayName),
    path: firstParentPath,
  };
};

/**
 * Process supply subfolders and group them if there are more than SUBFOLDER_COLLAPSE_THRESHOLD
 */
const processSupplySubfolders = (supplySubfolders, basePathNoMultipleDays) => {
  if (!supplySubfolders || supplySubfolders.length === 0) {
    return [];
  }

  // Sort subfolders by parent folder first to keep groups together, then by name
  const sortedSubfolders = [...supplySubfolders].sort((a, b) => {
    // Treat undefined parent (root items) as empty string
    const parentA = a.parentFolder || "";
    const parentB = b.parentFolder || "";

    // If parents differ, sort by parent
    if (parentA !== parentB) {
      return parentA.localeCompare(parentB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    }

    // If parents are same (or both root), sort by name
    return a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });

  // Map subfolders to folder objects
  const mappedSubfolders = sortedSubfolders.map((subfolder) => {
    // Handle loose supply folder (files directly in supply)
    if (subfolder.isLooseSupplyFolder) {
      return {
        name: subfolder.name,
        path: `/supply`,
        compact: true,
        isSupplySubfolder: true,
        isLooseSupplyFolder: true,
        isSmartDeepFolder: false,
        parentFolder: null,
        subfolderName: null,
      };
    }

    // Handle smart deep folders vs regular folders
    let folderPath;
    let displayName = subfolder.name;

    if (subfolder.isSmartDeepFolder) {
      // For smart deep folders, construct path using parent and subfolder names
      folderPath = `/supply/${subfolder.parentFolder}/${subfolder.subfolderName}`;
      // Prefix with parent folder name (e.g. "D1 - FolderName")
      if (subfolder.parentFolder) {
        displayName = `${subfolder.parentFolder} / ${subfolder.name}`;
      }
    } else {
      // Use traditional path for regular folders
      folderPath = `/supply/${subfolder.name}`;
    }

    return {
      name: displayName,
      path: folderPath,
      compact: true,
      isSupplySubfolder: true,
      isSmartDeepFolder: subfolder.isSmartDeepFolder || false,
      parentFolder: subfolder.parentFolder || null,
      subfolderName: subfolder.subfolderName || null,
    };
  });

  // If there are more than SUBFOLDER_COLLAPSE_THRESHOLD subfolders, wrap them in a parent
  if (mappedSubfolders.length > SUBFOLDER_COLLAPSE_THRESHOLD) {
    const metadata = getCollapsibleParentMetadata(mappedSubfolders);
    const collapsibleParent = metadata || {
      name: DEFAULT_COLLAPSIBLE_PARENT_NAME,
      path: DEFAULT_COLLAPSIBLE_PARENT_PATH,
    };

    // If metadata exists, the path is derived from subfolders which are absolute
    const isAbsolute = !!metadata;

    return [{
      name: collapsibleParent.name,
      path: collapsibleParent.path,
      isCollapsibleParent: true,
      compact: true,
      subfolders: mappedSubfolders,
      subfolderCount: mappedSubfolders.length,
      basePathNoMultipleDays: basePathNoMultipleDays,
      isSupplySubfolder: true,
    }];
  }

  return mappedSubfolders;
};

const useDropboxFolders = (allJobData, jobID) => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [labels] = useAtom(labelsAtom);

  useEffect(() => {
    const fetchFolders = async () => {
      if (!allJobData || allJobData.length === 0 || !jobID) {
        setFolders([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const _t0 = performance.now();
        const allFoldersResult = [];
        const representativeJob = allJobData[0];
        const basePathNoMultipleDays = `/${representativeJob.client}/${representativeJob.jobNumber}-${representativeJob.client}${representativeJob.jobTypeShort || ""}`;

        // Kick off Firestore ClientSelects check immediately — runs in parallel with supply fetch
        const clientSelectsExistsPromise = checkClientSelectsExists(jobID);

        // 1. Fetch Supply Folders (Once, Global)
        let supplyFolders = [];

        // Check for "supplied" label
        const suppliedLabelId = labels.find(
          (l) => l.name.toLowerCase() === "supplied"
        )?.id;

        const hasSuppliedLabel =
          suppliedLabelId &&
          allJobData.some(job =>
            (job.idLabels && job.idLabels.includes(suppliedLabelId)) ||
            (job.idLabelsPermanent && job.idLabelsPermanent.includes(suppliedLabelId))
          );

        // Check if we should fetch supply folders (if labelled supplied OR if specific folders are always visible)
        const alwaysVisibleFolders = (representativeJob.visibleFolders || []).map(p => p.toLowerCase());
        const hasAlwaysVisibleSupplyFolders = alwaysVisibleFolders.some(path =>
          path === '/supply' || path.startsWith('/supply/')
        );

        if (hasSuppliedLabel || hasAlwaysVisibleSupplyFolders) {
          const _tSupply = performance.now();
          try {
            const supplyResult = await listSupplyFoldersWithSmartDepth(
              `${basePathNoMultipleDays}/supply`
            );
            supplyFolders = supplyResult.entries || [];
          } catch (error) {
            console.warn("Error fetching Supply subfolders:", error);
          }
          console.log(`⏱️ [Portal] supply smart-depth total: ${(performance.now() - _tSupply).toFixed(0)}ms`);
        }

        if (supplyFolders.length > 0) {
          const processedSupplyFolders = processSupplySubfolders(
            supplyFolders,
            basePathNoMultipleDays
          );

          // Kick off the root /supply shared-link fetch in parallel with the per-subfolder fetches
          const rootSupplyPath = `${basePathNoMultipleDays}/supply`;
          const rootSupplyLinkPromise = getDropboxLink(rootSupplyPath, () => { }, false)
            .then((result) => (result && !result.error ? result : null))
            .catch((err) => {
              console.warn("Error fetching root supply link:", err);
              return null;
            });

          // Get excluded folders from job data (needed for filtering below)
          const excludedFolders = (representativeJob.excludedFolders || []).map(p => p.toLowerCase());

          // Shared visibility filter — same logic for both collapsible and non-collapsible
          const applyVisibilityFilter = (folders) => folders.filter((folder) => {
            const relativePath = folder.path
              .toLowerCase()
              .replace(basePathNoMultipleDays.toLowerCase(), "");
            const isExcluded = excludedFolders.some(excl =>
              relativePath === excl || relativePath.startsWith(excl + "/")
            );
            if (isExcluded) return false;
            const isAlwaysVisible = alwaysVisibleFolders.some(path =>
              relativePath === path || relativePath.startsWith(path + '/')
            );
            if (hasSuppliedLabel) return true;
            return isAlwaysVisible;
          });

          const isCollapsible = processedSupplyFolders.some(f => f.isCollapsibleParent);

          if (isCollapsible) {
            // CollapsibleParentFolder fetches its own links on demand — show immediately
            // without waiting for N getDropboxLink round trips.
            const filteredFolders = applyVisibilityFilter(processedSupplyFolders);
            if (filteredFolders.length > 0) {
              const rootSupplyLink = await rootSupplyLinkPromise;
              const supplyGroup = {
                dayLabel: "Supply",
                isSupplyGroup: true,
                jobId: "global-supply",
                folders: filteredFolders,
                multiDayOrder: -1,
                rootSupplyLink,
                rootSupplyExists: !!rootSupplyLink,
              };
              allFoldersResult.push(supplyGroup);
              setFolders([supplyGroup]);
            }
          } else {
            // ≤4 supply folders: fetch shared links in parallel then show
            const supplyFolderPromises = processedSupplyFolders.map(async (folder) => {
              const fullPath = `${basePathNoMultipleDays}${folder.path}`;
              try {
                const result = await getDropboxLink(fullPath, () => { }, false);
                if (result.error) {
                  return { ...folder, path: fullPath, exists: false, error: result.errorText, downloadUrl: null };
                }
                return { ...folder, path: fullPath, exists: true, downloadUrl: result, error: null };
              } catch (err) {
                return { ...folder, path: fullPath, exists: false, error: err.message, downloadUrl: null };
              }
            });

            const resolvedSupplyFolders = await Promise.all(supplyFolderPromises);
            const filteredSupplyFolders = applyVisibilityFilter(resolvedSupplyFolders);

            if (filteredSupplyFolders.length > 0) {
              const rootSupplyLink = await rootSupplyLinkPromise;
              const supplyGroup = {
                dayLabel: "Supply",
                isSupplyGroup: true,
                jobId: "global-supply",
                folders: filteredSupplyFolders,
                multiDayOrder: -1,
                rootSupplyLink,
                rootSupplyExists: !!rootSupplyLink,
              };
              allFoldersResult.push(supplyGroup);
              setFolders([supplyGroup]);
            }
          }
        }

        // 2. Process each job (day) in parallel and stream results as they arrive
        const isMultiDay = allJobData.length > 1;
        await Promise.all(allJobData.map(async (jobData) => {
          const dayLabel = jobData.multipleDays || jobData.multiDaySuffix || null;
          const _tDay = performance.now();

          const dayHasSuppliedLabel =
            suppliedLabelId &&
            ((jobData.idLabels && jobData.idLabels.includes(suppliedLabelId)) ||
              (jobData.idLabelsPermanent && jobData.idLabelsPermanent.includes(suppliedLabelId)));

          const dayFolders = await fetchFoldersForJob(jobData, false, dayHasSuppliedLabel, clientSelectsExistsPromise);
          console.log(`⏱️ [Portal] day "${dayLabel || "main"}": ${(performance.now() - _tDay).toFixed(0)}ms → ${dayFolders?.length ?? 0} folders`);

          if (dayFolders && dayFolders.length > 0) {
            const dayGroup = {
              dayLabel,
              jobId: jobData.id,
              multiDayOrder: jobData.multiDayOrder || 1,
              folders: dayFolders,
              isMultiDay,
            };
            allFoldersResult.push(dayGroup);
            // Stream this day's folders into state immediately so UI can render early
            setFolders((prev) => {
              const merged = [...prev.filter((g) => g.jobId !== dayGroup.jobId), dayGroup];
              return merged.sort((a, b) => (a.multiDayOrder ?? 0) - (b.multiDayOrder ?? 0));
            });
          }
        }));

        // Final sort: Supply first (order -1), then days by multiDayOrder
        allFoldersResult.sort((a, b) => a.multiDayOrder - b.multiDayOrder);

        console.log(`⏱️ [Portal] fetchFolders total: ${(performance.now() - _t0).toFixed(0)}ms`);
        console.log("🗂️ allFoldersResult (final):", allFoldersResult.map(group => ({
          dayLabel: group.dayLabel,
          isSupplyGroup: group.isSupplyGroup,
          jobId: group.jobId,
          folderCount: group.folders?.length,
          folderNames: group.folders?.map(f => f.name)
        })));

        setFolders(allFoldersResult);
      } catch (err) {
        setError(err.message || "Failed to load project folders");
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allJobData, jobID, labels]);

  // Extract and process folders for a single job/day
  const fetchFoldersForJob = async (jobData, fetchSupply = true, hasSuppliedLabel = false, clientSelectsExistsPromise = null) => {

    // Await the already-in-flight Firestore check (started before supply fetch)
    const _tCS = performance.now();
    const clientSelectsExists = await (clientSelectsExistsPromise ?? checkClientSelectsExists(jobID));
    console.log(`⏱️ [Portal] checkClientSelectsExists (awaited): ${(performance.now() - _tCS).toFixed(0)}ms → ${clientSelectsExists}`);

    let basePath = `/${jobData.client}/${jobData.jobNumber}-${jobData.client
      }${jobData.jobTypeShort || ""}${jobData.multipleDays ? `/${jobData.multipleDays}` : ""
      }`;

    const basePathNoMultipleDays = `/${jobData.client}/${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort || ""}`;

    if (
      jobData.jobTypeFull.includes("-P") &&
      !jobData.jobTypeFull.includes("-PC") &&
      !jobData.multipleDays
    ) {
      basePath = `/${jobData.client}/${jobData.jobNumber}-${jobData.client
        }${jobData.jobTypeShort || ""}`;
    }

    let supplySubfolders = [];
    if (fetchSupply) {
      try {
        const supplyResult = await listSupplyFoldersWithSmartDepth(
          `${basePathNoMultipleDays}/supply`
        );
        supplySubfolders = supplyResult.entries || [];
      } catch (error) {
        console.warn("Error fetching Supply subfolders:", error);
        supplySubfolders = [];
      }
    }

    // Create folder types in the exact order specified:
    // 1. Supply subfolders (if enabled)
    // 2. Content
    // 3. Storyboards
    // 4. Storyboard Selects
    // 5. Quickproofs

    let processedSupplyFolders = [];
    if (fetchSupply) {
      processedSupplyFolders = processSupplySubfolders(supplySubfolders, basePathNoMultipleDays);
    }

    let folderTypes = [
      ...processedSupplyFolders,
      { name: "Content", path: "/content", compact: true },
      { name: "Early Supply", path: "/early supply", compact: true },
      { name: "Storyboards", path: "/storyboards" },
      {
        name: "Storyboard Selects",
        path: "/storyboard selects",
        compact: true,
      },
      { name: "Quickproofs", path: "/quickproofs", compact: true },
    ];

    if (!clientSelectsExists && jobData.jobTypeFull.includes("-P")) {
      if (supplySubfolders.length > 0) {
        const processedSupplyFoldersProduct = processSupplySubfolders(supplySubfolders, basePathNoMultipleDays);
        folderTypes = [...processedSupplyFoldersProduct];
      } else {
        folderTypes = [{ name: "Storyboards", path: "/storyboards" }];
      }
    }

    if (!fetchSupply) {
      folderTypes = folderTypes.filter(f => !f.isSupplySubfolder && !f.isCollapsibleParent);
    }

    // Add custom folders from suppliedFolders that aren't in the predefined list
    const suppliedFolderPaths = (jobData.suppliedFolders || DEFAULT_SUPPLIED_FOLDERS).map(p => p.toLowerCase());
    const existingPaths = folderTypes.map(f => f.path.toLowerCase());

    console.log("🗂️ suppliedFolderPaths:", suppliedFolderPaths);
    console.log("🗂️ existingPaths:", existingPaths);

    // Include BOTH root-level custom folders AND nested subfolders as standalone entries
    // This allows subfolders to be visible even if their parent is hidden
    const customFolders = suppliedFolderPaths
      .filter(path => {
        // Exclude predefined root folders that are already in folderTypes
        const notAlreadyIncluded = !existingPaths.includes(path);
        // Exclude supply subfolders (those starting with /supply/) - handled separately
        const isNotSupplyPath = !path.startsWith('/supply/') && path !== '/supply';
        console.log(`🗂️ Checking path "${path}": notAlreadyIncluded=${notAlreadyIncluded}, isNotSupplyPath=${isNotSupplyPath}`);
        return notAlreadyIncluded && isNotSupplyPath;
      })
      .map(path => {
        // Get path parts
        const parts = path.split('/').filter(Boolean);
        const isSubfolder = parts.length > 1;

        // For subfolders, show the full path structure (e.g. "Test Extra / Test Folder22")
        // For root folders, just show the name
        let name;
        if (isSubfolder) {
          name = parts.map(part =>
            part.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
          ).join(' / ');
        } else {
          const folderName = parts[parts.length - 1];
          name = folderName.split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
        }

        console.log(`🗂️ Adding custom folder: "${name}" at path "${path}" (isSubfolder: ${isSubfolder})`);
        return {
          name,
          path,
          compact: true,
          isCustomFolder: true,
          isStandaloneSubfolder: isSubfolder,
          // Store the parent path for reference
          parentPath: isSubfolder ? '/' + parts.slice(0, -1).join('/') : null
        };
      });

    console.log("🗂️ Custom folders to add:", customFolders);

    // Sort custom folders alphabetically
    customFolders.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

    // Insert custom folders before Storyboards (find the index of Storyboards and insert there)
    const storyboardsIndex = folderTypes.findIndex(f => f.name === "Storyboards");
    if (storyboardsIndex >= 0 && customFolders.length > 0) {
      folderTypes = [
        ...folderTypes.slice(0, storyboardsIndex),
        ...customFolders,
        ...folderTypes.slice(storyboardsIndex)
      ];
    } else {
      // If no Storyboards found, just append at end
      folderTypes = [...folderTypes, ...customFolders];
    }

    const folderPromises = folderTypes.map(async (folderType) => {
      // Handle collapsible parent folders differently - they don't need Dropbox link checks
      if (folderType.isCollapsibleParent) {
        return {
          name: folderType.name,
          path: folderType.path,
          exists: true,
          downloadUrl: null, // Parent folders don't have direct download links
          error: null,
          compact: folderType.compact || false,
          isCollapsibleParent: true,
          subfolders: folderType.subfolders,
          subfolderCount: folderType.subfolderCount,
          basePathNoMultipleDays: folderType.basePathNoMultipleDays,
        };
      }

      // Use basePathNoMultipleDays for supply folders, basePath for others
      const pathBase = folderType.isSupplySubfolder ? basePathNoMultipleDays : basePath;

      // Check if path is already absolute (starts with pathBase)
      // This handles cases where we use full Dropbox paths (e.g. from search results or smart folders)
      // We also trust isSupplySubfolder because we now use full paths for all supply folders
      const isAbsolute = folderType.isAbsolutePath ||
        (folderType.path &&
          pathBase &&
          folderType.path.toLowerCase().startsWith(pathBase.toLowerCase()));

      const fullPath = isAbsolute ? folderType.path : `${pathBase}${folderType.path}`;

      try {
        const _tLink = performance.now();
        const result = await getDropboxLink(fullPath, () => { }, false);
        console.log(`⏱️ [Portal] getDropboxLink "${folderType.name}": ${(performance.now() - _tLink).toFixed(0)}ms → ${result?.error ? "not found" : "ok"}`);
        if (result.error) {
          return {
            name: folderType.name,
            path: fullPath,
            exists: false,
            downloadUrl: null,
            error: result.errorText,
            compact: folderType.compact || false,
            isSupplySubfolder: folderType.isSupplySubfolder || false,
            isLooseSupplyFolder: folderType.isLooseSupplyFolder || false,
          };
        }
        return {
          name: folderType.name,
          path: fullPath,
          exists: true,
          downloadUrl: result,
          error: null,
          compact: folderType.compact || false,
          isSupplySubfolder: folderType.isSupplySubfolder || false,
          isLooseSupplyFolder: folderType.isLooseSupplyFolder || false,
          isSmartDeepFolder: folderType.isSmartDeepFolder || false,
          parentFolder: folderType.parentFolder || null,
          subfolderName: folderType.subfolderName || null,
        };
      } catch (err) {
        return {
          name: folderType.name,
          path: fullPath,
          exists: false,
          downloadUrl: null,
          error: err.message,
          compact: folderType.compact || false,
          isSupplySubfolder: folderType.isSupplySubfolder || false,
          isLooseSupplyFolder: folderType.isLooseSupplyFolder || false,
          isSmartDeepFolder: folderType.isSmartDeepFolder || false,
          parentFolder: folderType.parentFolder || null,
          subfolderName: folderType.subfolderName || null,
        };
      }
    });

    const folderResults = await Promise.all(folderPromises);
    console.log("🗂️ folderResults (all):", folderResults.map(f => ({ name: f.name, path: f.path, exists: f.exists, error: f.error })));
    const existingFolders = folderResults.filter((folder) => folder.exists);
    console.log("🗂️ existingFolders (filtered):", existingFolders.map(f => f.name));

    // Get included/excluded folders from job data (with defaults)
    const includedFolders = (jobData.visibleFolders || DEFAULT_VISIBLE_FOLDERS).map(p => p.toLowerCase());
    const excludedFolders = (jobData.excludedFolders || []).map(p => p.toLowerCase());

    // Check for "supplied" label to determine if we show all folders
    // Note: labels atom is accessed at hook level, we need to pass this info
    // For now, we'll check if the job has been fully supplied via the parent hook logic

    // Filter folders based on visibility rules:
    // 1. Excluded folders are NEVER shown
    // 2. Folders in includedFolders are ALWAYS shown
    // 3. Other folders only shown if "Supplied" label present (handled at parent level)
    const visibleFolders = existingFolders.filter((folder) => {
      // Get the relative path for matching (e.g., /storyboards, /content)
      const relativePath = folder.path
        .toLowerCase()
        .replace(basePath.toLowerCase(), "")
        .replace(basePathNoMultipleDays.toLowerCase(), "");

      // Check if this folder or any parent is excluded
      const isExcluded = excludedFolders.some(excl =>
        relativePath === excl ||
        relativePath.startsWith(excl + "/")
      );

      // Check if this folder is in includedFolders (always visible)
      const isIncluded = includedFolders.some(inc =>
        relativePath === inc ||
        relativePath.startsWith(inc + "/")
      );

      console.log(`🗂️ Visibility check for "${folder.name}": path="${folder.path}", relativePath="${relativePath}", isExcluded=${isExcluded}, isIncluded=${isIncluded}, hasSuppliedLabel=${hasSuppliedLabel}`);

      if (isExcluded) return false;
      if (isIncluded) return true;

      // For supply subfolders, visibility is handled here now (granular)
      // Removed the bypass logic so they respect visibleFolders/hasSuppliedLabel logic
      // if (folder.isSupplySubfolder) return true;

      // Folder is not in includedFolders and not excluded
      // Only show if "Supplied" label is present on the job
      return hasSuppliedLabel;
    });

    // Sort folders alphabetically by name - REMOVED to preserve manual order
    // visibleFolders.sort((a, b) => a.name.localeCompare(b.name));

    console.log("🗂️ visibleFolders (sorted):", visibleFolders.map(f => f.name));

    // Return the visible folders for this day
    return visibleFolders;
  };

  // Helper function to get a specific folder by name
  const getFolderByName = (name) => {
    return folders.find((folder) => folder.name === name);
  };

  // Helper function to check if any folders exist
  const hasAnyFolders = () => {
    return folders.length > 0;
  };

  return {
    folders,
    loading,
    error,
    getFolderByName,
    hasAnyFolders,
  };
};

export default useDropboxFolders;
